/**
 * Opening geometry math (docs/bim-plan/01 §4/§5, Addendum 2): door/window profiles in the
 * host wall's ELEVATION plane — coordinates are (s, z) where s runs along the wall
 * centerline and z is world elevation. Arched/segmental heads are ARCS IN THIS PLANE,
 * tessellated to segments at tolerance before any boolean — Clipper2 never sees a true
 * arc (the same flattening rule as Path2D.bulge, which curves the PLAN path instead).
 *
 * The wall solid strategy (keeps Phase-1 miters intact):
 *   body   = plan outline MINUS full-height slot rects at each opening → prism
 *   header = elevation-profile piece above the opening head (arch bite subtracted here)
 *   sill   = elevation-profile piece below a window sill
 * Header/sill pieces are extruded ACROSS the wall thickness (extrudeProfile.ts).
 */
import type { ParamValue, Path2D, Pt2, ResolvedElement } from '@aj/bim-kernel'
import { resolveOpeningFrame, flattenPathPts, DEFAULT_ARC_TOL_FT, isCantedWallParams } from '@aj/bim-kernel'

const ARC_TOL_FT = 1 / 96 // 1/8" chord tolerance

export interface OpeningPlacement {
  elementId: string
  /** along-wall range of the opening (feet from the wall path start) */
  s0: number
  s1: number
  /** center s (clamped so the opening fits its segment) */
  center: number
  /** vertical extent of the opening VOID (rect part; arch rises above zHead) */
  zLow: number
  zHead: number
  /** arch rise above zHead (0 = flat head) */
  riseFt: number
  /** RAKED WINDOW HEADS round: signed straight-line rise above zHead (0 = not
   *  raked) — see headRake's doc comment for the sign convention. Mutually
   *  exclusive with riseFt by construction (headType is one choice). */
  rakeFt: number
  widthFt: number
}

/** rise for the head: arched = semicircle capped at w/2; segmental = the type's rise (clamped). */
export function headRise(headType: string, headRiseFt: number, widthFt: number): number {
  // HALF-ROUND: always a semicircle over this span, whatever its width
  if (headType === 'halfRound') return Math.max(0, widthFt / 2)
  if (headType === 'arched') return Math.min(Math.max(headRiseFt, 0), widthFt / 2)
  if (headType === 'segmental') return Math.min(Math.max(headRiseFt, 0), widthFt / 2)
  return 0
}

/**
 * RAKED WINDOW HEADS round (Matt, 2026-09-22): signed straight-line rise for a
 * 'raked' head — positive = higher at s1 (right), negative = higher at s0 (left),
 * zero for every other headType. Reads the instance's own `rakeSlopeRise` override
 * first, falling back to the project default (`ResolvedElement.windowRake`,
 * attached only for windows — the `coverageDepthFt` shadow-default pattern), then
 * a hardcoded default, matching a roof's own `slopeRise ?? defaultSlopeRise ?? 6`
 * fallback chain exactly. `runFt` is the horizontal span the slope applies over —
 * callers pass the SAME width used for `headRise` above, so the wall's own void
 * and the window unit's own geometry (protos.ts) always agree on the exact slope.
 * Clamped to `[0, runFt]` as a sanity ceiling, not a design rule.
 *
 * Takes a structural subset of ResolvedElement (params + optional windowRake) —
 * satisfied by a real ResolvedElement, but also by a bare `{ params }` wrapper
 * around a schedule row's/type-editor's plain params bag (elevationFigure.ts's
 * windowFigure — no model handle there, so no windowRake; falls through to the
 * hardcoded default, same as protos.ts's OpeningPreview3D isolated-preview path).
 */
export function headRake(opening: { params: Record<string, ParamValue>; windowRake?: { slopeRise: number } }, runFt: number): number {
  if (String(opening.params.headType || 'flat') !== 'raked') return 0
  const slope = (opening.params.rakeSlopeRise as number | null | undefined)
    ?? opening.windowRake?.slopeRise
    ?? 6
  const rise = Math.min(Math.max((slope / 12) * runFt, 0), Math.max(runFt, 0))
  return opening.params.rakeHigh === 'L' ? -rise : rise
}

/**
 * Tessellated head arc from (s0, 0) up to rise and down to (s1, 0), in LOCAL coords
 * relative to the head line. Returned left-to-right EXCLUDING the two endpoints.
 */
export function headArcPoints(s0: number, s1: number, rise: number): Pt2[] {
  const w = s1 - s0
  if (rise <= 1e-9 || w <= 1e-9) return []
  const half = w / 2
  const R = (rise * rise + half * half) / (2 * rise)
  const cy = rise - R // center below the head line when segmental
  const cx = (s0 + s1) / 2
  // angles from center to the two springing points
  const a0 = Math.atan2(0 - cy, s0 - cx)
  const a1 = Math.atan2(0 - cy, s1 - cx)
  // sweep over the top (through +90°)
  let sweep = a1 - a0
  if (sweep > 0) sweep -= Math.PI * 2
  const maxStep = 2 * Math.acos(Math.max(-1, Math.min(1, 1 - ARC_TOL_FT / R)))
  const steps = Math.max(4, Math.ceil(Math.abs(sweep) / Math.max(maxStep, 1e-3)))
  const pts: Pt2[] = []
  for (let i = 1; i < steps; i++) {
    const a = a0 + (sweep * i) / steps
    pts.push([cx + R * Math.cos(a), cy + R * Math.sin(a)])
  }
  return pts
}

/**
 * The opening VOID profile in (s, z) — rect plus tessellated arch head, or (RAKED
 * WINDOW HEADS round) a straight sloped head. CCW. Used for glazing/leaf prototype
 * shapes and (via difference) plan symbols.
 */
export function openingProfile(p: OpeningPlacement): Pt2[] {
  if (p.rakeFt) {
    const zLeft = p.zHead + Math.max(0, -p.rakeFt)
    const zRight = p.zHead + Math.max(0, p.rakeFt)
    return [[p.s0, p.zLow], [p.s1, p.zLow], [p.s1, zRight], [p.s0, zLeft]]
  }
  const out: Pt2[] = [[p.s0, p.zLow], [p.s1, p.zLow], [p.s1, p.zHead]]
  const arc = headArcPoints(p.s0, p.s1, p.riseFt)
  for (let i = arc.length - 1; i >= 0; i--) out.push([arc[i][0], p.zHead + arc[i][1]])
  out.push([p.s0, p.zHead])
  return out
}

/**
 * Header piece profile: from the opening head up to the wall top, with the arch (or,
 * RAKED WINDOW HEADS round, the straight slope) bitten out of its underside. Returns
 * null when there is no room for a header.
 */
export function headerProfile(p: OpeningPlacement, zWallTop: number): Pt2[] | null {
  if (p.rakeFt) {
    const zLeft = p.zHead + Math.max(0, -p.rakeFt)
    const zRight = p.zHead + Math.max(0, p.rakeFt)
    if (zWallTop <= Math.max(zLeft, zRight) + 1e-6) return null
    return [[p.s0, zLeft], [p.s1, zRight], [p.s1, zWallTop], [p.s0, zWallTop]]
  }
  const topOfVoid = p.zHead + p.riseFt
  if (zWallTop <= topOfVoid + 1e-6) return null
  const out: Pt2[] = [[p.s0, p.zHead]]
  const arc = headArcPoints(p.s0, p.s1, p.riseFt)
  for (const [x, y] of arc) out.push([x, p.zHead + y])
  out.push([p.s1, p.zHead], [p.s1, zWallTop], [p.s0, zWallTop])
  return out
}

/** Sill piece profile below a window (null for door-style openings starting at the base). */
export function sillProfile(p: OpeningPlacement, zWallBase: number): Pt2[] | null {
  if (p.zLow <= zWallBase + 1e-6) return null
  return [[p.s0, zWallBase], [p.s1, zWallBase], [p.s1, p.zLow], [p.s0, p.zLow]]
}

// ---------- placement along the host wall ----------

export interface WallFrame {
  /** world point at along-wall distance s, and the unit direction of that segment */
  pointAt(s: number): { pt: Pt2; dir: Pt2 }
  totalLength: number
  /** clamp an opening [center, width] so it sits fully on ONE straight segment —
   *  or, with `ext` (DOOR ACROSS A SEAM, below), may hang past the wall's start/end
   *  onto a collinear wall continuing from there, by at most half its width */
  clampToSegment(center: number, width: number, ext?: SeamExt | null): { center: number; segIndex: number } | null
  /** pointAt, but continued straight past the wall's ends along its end segments */
  pointAtExt(s: number): { pt: Pt2; dir: Pt2 }
}

/** how far an opening may run past this wall's start (`before`) / end (`after`) */
export interface SeamExt { before: number; after: number }

export function wallFrame(centerline: Pt2[]): WallFrame {
  const segs: { a: Pt2; b: Pt2; len: number; start: number; dir: Pt2 }[] = []
  let acc = 0
  for (let i = 0; i < centerline.length - 1; i++) {
    const a = centerline[i], b = centerline[i + 1]
    const len = Math.hypot(b[0] - a[0], b[1] - a[1])
    if (len < 1e-9) continue
    segs.push({ a, b, len, start: acc, dir: [(b[0] - a[0]) / len, (b[1] - a[1]) / len] })
    acc += len
  }
  const totalLength = acc
  function segFor(s: number) {
    for (const seg of segs) {
      if (s <= seg.start + seg.len + 1e-9) return seg
    }
    return segs[segs.length - 1] ?? null
  }
  function pointAt(s: number): { pt: Pt2; dir: Pt2 } {
    const seg = segFor(Math.max(0, Math.min(s, totalLength)))
    if (!seg) return { pt: [0, 0], dir: [1, 0] }
    const local = Math.max(0, Math.min(s - seg.start, seg.len))
    return { pt: [seg.a[0] + seg.dir[0] * local, seg.a[1] + seg.dir[1] * local], dir: seg.dir }
  }
  return {
    totalLength,
    pointAt,
    pointAtExt(s: number) {
      const first = segs[0], last = segs[segs.length - 1]
      if (first && s < 0) return { pt: [first.a[0] + first.dir[0] * s, first.a[1] + first.dir[1] * s], dir: first.dir }
      if (last && s > totalLength) {
        const d = s - totalLength
        return { pt: [last.b[0] + last.dir[0] * d, last.b[1] + last.dir[1] * d], dir: last.dir }
      }
      return pointAt(s)
    },
    clampToSegment(center: number, width: number, ext?: SeamExt | null) {
      const before = ext?.before || 0, after = ext?.after || 0
      if (before > 0 || after > 0) {
        // DOOR ACROSS A SEAM: the CENTER stays on this wall ([0, L]) so every
        // station consumer (grips, tags, picking) keeps reading a point on the host;
        // only the first/last segment may overhang, and only into its seam partner
        if (!segs.length) return null
        const first = segs[0], last = segs[segs.length - 1]
        let c = Math.max(Math.max(0, width / 2 - before), Math.min(center, Math.min(totalLength, totalLength - width / 2 + after)))
        const seg = segFor(c)
        if (!seg) return null
        const lo = seg === first ? Math.max(0, seg.start + width / 2 - before) : seg.start + width / 2
        const hi = seg === last ? Math.min(totalLength, seg.start + seg.len - width / 2 + after) : seg.start + seg.len - width / 2
        if (hi + 1e-9 < lo) return null
        c = Math.max(lo, Math.min(c, hi))
        return { center: c, segIndex: segs.indexOf(seg) }
      }
      if (!segs.length || width > totalLength) return null
      let c = Math.max(width / 2, Math.min(center, totalLength - width / 2))
      const seg = segFor(c)
      if (!seg) return null
      // fit fully on this segment when it can hold the opening
      if (seg.len + 1e-9 >= width) {
        c = Math.max(seg.start + width / 2, Math.min(c, seg.start + seg.len - width / 2))
        return { center: c, segIndex: segs.indexOf(seg) }
      }
      return null // segment too short — caller skips the opening (validate warning)
    },
  }
}

/** Resolve one hosted opening's placement against its host wall. Null = doesn't fit. */
export function placeOpening(
  opening: ResolvedElement,
  frame: WallFrame,
  zWallBase: number,
): OpeningPlacement | null {
  const widthTotal = totalOpeningWidth(opening)
  // DOOR ACROSS A SEAM: hostedCuttingMap stamps the seam onto the copy each wall
  // receives — the HOST's copy carries how far it may overhang (`__seamExt`), the
  // PARTNER's copy carries the host's already-resolved span mapped into the
  // partner's own stations (`__seamGuest` — never re-clamped, it is not ours)
  const guest = opening.params.__seamGuest as unknown as { s0: number; s1: number } | undefined
  const clamped = guest
    ? { center: (guest.s0 + guest.s1) / 2, segIndex: 0 }
    : frame.clampToSegment((opening.params.hostOffsetFt as number) || 0, widthTotal,
      opening.params.__seamExt as unknown as SeamExt | undefined)
  if (!clamped) return null
  const heightFt = (opening.params.heightFt as number) || 6.667
  // sillFt (2026-08-09): height above the wall/floor base — windows have always had
  // it; doors gained it the same round (shower doors, counter-height pass-throughs,
  // platform-mounted mechanical doors). Same field, same semantics, either category.
  const sill = (opening.params.sillFt as number) || 0
  const transom = opening.params.transom === true ? ((opening.params.transomHeightFt as number) || 1) : 0
  const rise = headRise(
    String(opening.params.headType || 'flat'),
    (opening.params.headRiseFt as number) || 0,
    widthTotal,
  )
  const rake = headRake(opening, widthTotal)
  // LEVEL-INDEPENDENT OPENINGS (2026-08-17, Matt's report): the schema's own doc
  // comment always said "level base + sillFt" — meaning the OPENING's own level,
  // not the host wall's. A wall commonly spans several stories (based at a lower
  // level, extending up through an upper one to the roof); an opening hosted on
  // it must place from ITS OWN assigned level, independent of where the wall
  // itself starts. `zWallBase` is now only a graceful-degradation fallback for
  // the rare case the opening's level fails to resolve (a deleted level) — every
  // normal opening ignores it entirely in favor of `opening.level.elevationFt`.
  const zBase = opening.level?.elevationFt ?? zWallBase
  return {
    elementId: opening.id,
    s0: clamped.center - widthTotal / 2,
    s1: clamped.center + widthTotal / 2,
    center: clamped.center,
    zLow: zBase + sill,
    zHead: zBase + sill + heightFt + transom,
    riseFt: rise,
    rakeFt: rake,
    widthFt: widthTotal,
  }
}

/**
 * Rough-opening width — the actual wall-void span, including the real frame
 * (DOOR REALISM round follow-up, 2026-08-25, Matt's report: "the opening drawn on
 * plans shows the width including the frame, so the door is represented as the
 * wrong size"). `widthFt` is the SCHEDULE size — the leaf/sash's own clear width —
 * never the rough opening; the frame (openingFrame.ts's resolved product width)
 * is ADDED outward from it, on both jambs, so the rough opening is always bigger
 * than the nominal door/window size, never equal to it.
 *
 * Sidelights (doors only, SIDELIGHTS PER-SIDE round) use the same single-shared-
 * jamb convention plan.ts already drew: a sidelight's INNER edge butts directly
 * against the door's own jamb face (that jamb IS the mullion — no doubled frame
 * at the seam), so only its OUTER edge adds one more frame member. Each present
 * side therefore contributes its own clear width plus exactly one extra `fw`.
 */
export function totalOpeningWidth(opening: ResolvedElement): number {
  const w = (opening.params.widthFt as number) || 3
  const fw = resolveOpeningFrame(opening.category, { ...opening.typeParams, ...opening.params }).widthFt
  let total = w + 2 * fw
  if (opening.category === 'door') {
    if (opening.params.sidelightLeft === true) total += ((opening.params.sidelightLeftWidthFt as number) || 1.25) + fw
    if (opening.params.sidelightRight === true) total += ((opening.params.sidelightRightWidthFt as number) || 1.25) + fw
  }
  // FRAME ROUNDING (2026-10-08): the outside frame dimension is always a whole 1/8" —
  // jambs already snap to 1/16" (openingFrame.ts), this also absorbs a typed unit width
  // (an already-clean total is returned untouched: re-deriving it through the rounding can
  // land one float ulp off the frame geometry's own w + 2·fw and seam the edges)
  const r = Math.round(total * 96) / 96
  return Math.abs(r - total) < 1e-9 ? total : r
}

// ---------- corner-meeting sliders (Tier 2, 2026-08-08 — docs/MODELER.md design
// record): ONE element spanning TWO host walls. hostId = the leg-A wall,
// params.hostBId = the leg-B wall; the CORNER is never stored — it re-derives from
// stored-path endpoint coincidence (the cornerWeld convention), so wall edits track
// and separating the walls degrades the unit to a plain single-host placement. ----------

/** matches cornerWeld.CORNER_TOL — endpoint coincidence is THE corner definition */
const CORNER_FIT_TOL = 0.05

export function isCornerSliderUnit(el: ResolvedElement): boolean {
  return el.category === 'door' && String(el.typeParams.operation) === 'cornerSlider'
}

export interface CornerLegFit {
  /** corner station on THIS wall's stored-path frame (0 or totalLength) */
  sCorner: number
  /** station direction from the corner into the wall body */
  dirIn: 1 | -1
  /** this wall's clear leg width (clamped to the wall length) */
  legFt: number
  panelCount: number
  /** true = this wall is the unit's PRIMARY host (leg A; emits the 3D instance) */
  primary: boolean
}

/**
 * Resolve one corner unit's leg on ONE of its walls. `wallPts`/`partnerPts` are the
 * two walls' flattened STORED paths (hostOffset/corner semantics ride the reference
 * line, 06 §4.2). Null when the two walls share no coincident endpoint — the caller
 * degrades to normal single-host placement (the witness-degradation convention).
 */
export function cornerSliderLegFit(
  unit: ResolvedElement,
  wallId: string,
  wallPts: Pt2[],
  partnerPts: Pt2[],
): CornerLegFit | null {
  if (wallPts.length < 2 || partnerPts.length < 2) return null
  const len = (pts: Pt2[]): number => {
    let acc = 0
    for (let i = 0; i < pts.length - 1; i++) acc += Math.hypot(pts[i + 1][0] - pts[i][0], pts[i + 1][1] - pts[i][1])
    return acc
  }
  const wallLen = len(wallPts)
  const wEnds: { s: number; pt: Pt2 }[] = [
    { s: 0, pt: wallPts[0] },
    { s: wallLen, pt: wallPts[wallPts.length - 1] },
  ]
  const pEnds: Pt2[] = [partnerPts[0], partnerPts[partnerPts.length - 1]]
  let best: { s: number; d: number } | null = null
  for (const we of wEnds) {
    for (const pe of pEnds) {
      const d = Math.hypot(we.pt[0] - pe[0], we.pt[1] - pe[1])
      if (d < CORNER_FIT_TOL && (!best || d < best.d)) best = { s: we.s, d }
    }
  }
  if (!best) return null
  const primary = unit.hostId === wallId
  const rawLeg = primary
    ? ((unit.params.widthFt as number) || 8)
    : ((unit.params.legBFt as number) || 6)
  const panelCount = primary
    ? Math.max(1, Math.round((unit.params.panelCount as number) || 3))
    : Math.max(1, Math.round((unit.params.panelCountB as number) || 2))
  return {
    sCorner: best.s,
    dirIn: best.s === 0 ? 1 : -1,
    legFt: Math.min(rawLeg, wallLen),
    panelCount,
    primary,
  }
}

/** the corner unit's leg-B chirality: 'L' when wall B's inward direction turns LEFT
 *  of wall A's (local +y in the prototype frame), 'R' when it turns right */
export function cornerVariantOf(aInward: Pt2, bInward: Pt2): 'L' | 'R' {
  return aInward[0] * bInward[1] - aInward[1] * bInward[0] >= 0 ? 'L' : 'R'
}

/** inward unit direction of a polyline at one of its ends */
export function inwardDirAtEnd(pts: Pt2[], atStart: boolean): Pt2 {
  const a = atStart ? pts[0] : pts[pts.length - 1]
  const b = atStart ? pts[1] : pts[pts.length - 2]
  const d = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1
  return [(b[0] - a[0]) / d, (b[1] - a[1]) / d]
}

/** `idA|idB` (sorted) → the corner unit element, for the weld/patch suppression
 *  passes (the corner stays OPEN below the unit's head) */
export function cornerUnitsByWallPair(byId: Map<string, ResolvedElement>): Map<string, ResolvedElement> {
  const out = new Map<string, ResolvedElement>()
  byId.forEach((e) => {
    if (!isCornerSliderUnit(e)) return
    const hostB = typeof e.params.hostBId === 'string' ? e.params.hostBId : ''
    if (!e.hostId || !hostB) return
    const key = [e.hostId, hostB].sort().join('|')
    out.set(key, e)
  })
  return out
}

// ---------- OPENING FIT (2026-09-29) ----------
// Matt: two 16' sectional doors on the Santiago model "are not showing … I don't see
// them at all". Each sat on a wall shorter than the door — `placeOpening` returned null
// and every consumer silently skipped it. These two helpers are the one fit test the
// warning overlay and the size picker share, built on the same `wallFrame` +
// `clampToSegment` rule the drawing uses (plan.ts frames on the stored path).

/** a host wall's longest straight run — an opening must fit on ONE segment */
function longestSegment(pts: Pt2[]): number {
  let best = 0
  for (let i = 0; i < pts.length - 1; i++) {
    best = Math.max(best, Math.hypot(pts[i + 1][0] - pts[i][0], pts[i + 1][1] - pts[i][1]))
  }
  return best
}

const hostPathPts = (host: ResolvedElement | null | undefined): Pt2[] | null => {
  const path = host?.category === 'wall' ? (host.params.path as Path2D | undefined) : undefined
  return path?.nodes?.length ? flattenPathPts(path, DEFAULT_ARC_TOL_FT) : null
}

/**
 * The widest CLEAR (schedule) width `opening` can take on `host` — its longest straight
 * segment less the frame and any sidelights. null = no answer (not a wall, a corner
 * slider — its legs follow their own rule).
 */
export function openingMaxWidthFt(opening: ResolvedElement, host: ResolvedElement | null | undefined): number | null {
  if (isCornerSliderUnit(opening)) return null
  const pts = hostPathPts(host)
  if (!pts) return null
  const extra = totalOpeningWidth(opening) - ((opening.params.widthFt as number) || 3)
  return Math.max(0, longestSegment(pts) - extra)
}

export interface OpeningMisfit {
  elementId: string
  hostId: string
  /** the rough opening it needs vs the longest straight run the wall has */
  needFt: number
  haveFt: number
  /** where it WOULD sit: centered at its stored station (clamped onto the wall) */
  center: Pt2
  dir: Pt2
  zLow: number
  zHigh: number
}

/** non-null when `opening` doesn't fit `host` — i.e. placeOpening skips it */
export function openingMisfit(opening: ResolvedElement, host: ResolvedElement | null | undefined): OpeningMisfit | null {
  if (isCornerSliderUnit(opening) || !opening.hostId) return null
  const pts = hostPathPts(host)
  if (!pts || pts.length < 2) return null
  const frame = wallFrame(pts)
  if (placeOpening(opening, frame, 0)) return null
  const s = Math.max(0, Math.min((opening.params.hostOffsetFt as number) || 0, frame.totalLength))
  const at = frame.pointAt(s)
  const zBase = opening.level?.elevationFt ?? 0
  const sill = (opening.params.sillFt as number) || 0
  return {
    elementId: opening.id, hostId: opening.hostId,
    needFt: totalOpeningWidth(opening), haveFt: longestSegment(pts),
    center: at.pt, dir: at.dir,
    zLow: zBase + sill, zHigh: zBase + sill + ((opening.params.heightFt as number) || 6.667),
  }
}

// ---------- DOOR ACROSS A SEAM (2026-10-07) ----------
// A team report: a new door placed where an EXISTING wall meets a NEW wall could sit
// only on one wall or the other — never across the joint. Phasing lives on the
// element, so an existing-to-remain run continued in new work is two collinear walls
// joined end to end, and every opening was clamped onto ONE wall. Now an opening may
// hang past its host's start/end onto a wall that CONTINUES IN A STRAIGHT LINE from
// there (same level, end points coincide, same direction), by at most half its
// width — the center stays on the host, so every station consumer (grips, tags,
// picking, the stored hostOffsetFt itself) keeps reading a point on the host wall.
// Both walls cut: the host carries the opening (instance, plan symbol, trim), the
// partner only takes the slot over the overhang. Nothing is stored — the seam is
// read off the live wall geometry on every build, so moving either wall re-derives.

const SEAM_TOL_FT = 0.05

export interface SeamSide {
  partnerId: string
  /** the partner's straight run from the joint — the most the opening can use */
  extFt: number
  /** true = the partner's station 0 is at the joint */
  partnerAtStart: boolean
  partnerLen: number
}

function seamWallPts(el: ResolvedElement): Pt2[] | null {
  if (el.category !== 'wall') return null
  const path = el.params.path as Path2D | undefined
  if (!path?.nodes || path.nodes.length < 2 || path.closed) return null
  if (isCantedWallParams(el.params)) return null
  const pts = flattenPathPts(path, DEFAULT_ARC_TOL_FT)
  if (pts.length < 2) return null
  const a = pts[0], b = pts[pts.length - 1]
  if (Math.hypot(a[0] - b[0], a[1] - b[1]) < 1e-6) return null   // a loop has no free end
  return pts
}

function polyLength(pts: Pt2[]): number {
  let L = 0
  for (let i = 0; i + 1 < pts.length; i++) L += Math.hypot(pts[i + 1][0] - pts[i][0], pts[i + 1][1] - pts[i][1])
  return L
}

function seamAt(host: ResolvedElement, endPt: Pt2, outDir: Pt2, walls: ResolvedElement[]): SeamSide | null {
  for (const w of walls) {
    if (w.id === host.id || w.levelId !== host.levelId) continue
    const pts = seamWallPts(w)
    if (!pts) continue
    for (const atStart of [true, false]) {
      const p = atStart ? pts[0] : pts[pts.length - 1]
      const q = atStart ? pts[1] : pts[pts.length - 2]
      if (Math.hypot(p[0] - endPt[0], p[1] - endPt[1]) > SEAM_TOL_FT) continue
      const len = Math.hypot(q[0] - p[0], q[1] - p[1])
      if (len < 1e-6) continue
      if (((q[0] - p[0]) * outDir[0] + (q[1] - p[1]) * outDir[1]) / len < 0.9995) continue
      return { partnerId: w.id, extFt: len, partnerAtStart: atStart, partnerLen: polyLength(pts) }
    }
  }
  return null
}

/** the collinear walls continuing from `host`'s start and end, if any */
export function wallSeams(host: ResolvedElement, walls: ResolvedElement[]): { start: SeamSide | null; end: SeamSide | null } {
  const pts = seamWallPts(host)
  if (!pts) return { start: null, end: null }
  const unit = (a: Pt2, b: Pt2): Pt2 => {
    const d = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1
    return [(b[0] - a[0]) / d, (b[1] - a[1]) / d]
  }
  return {
    start: seamAt(host, pts[0], unit(pts[1], pts[0]), walls),
    end: seamAt(host, pts[pts.length - 1], unit(pts[pts.length - 2], pts[pts.length - 1]), walls),
  }
}

/** the SeamExt `host` offers its openings (0 where nothing continues) */
export function wallSeamExt(host: ResolvedElement, walls: ResolvedElement[]): SeamExt {
  const s = wallSeams(host, walls)
  return { before: s.start?.extFt ?? 0, after: s.end?.extFt ?? 0 }
}

export interface OpeningSeamSplit {
  /** the copy the host wall builds from (carries `__seamExt`) */
  host: ResolvedElement
  /** one copy per partner wall the opening actually overhangs (carries `__seamGuest`) */
  guests: { wallId: string; opening: ResolvedElement }[]
}

/**
 * Does this hosted opening overhang its host onto a seam partner? null = no (the
 * ordinary case — callers keep the element untouched). Doors and windows only;
 * corner sliders keep their own two-host rule.
 */
export function openingSeamSplit(
  opening: ResolvedElement, host: ResolvedElement | undefined, walls: ResolvedElement[],
): OpeningSeamSplit | null {
  if ((opening.category !== 'door' && opening.category !== 'window') || isCornerSliderUnit(opening)) return null
  if (!host) return null
  const pts = seamWallPts(host)
  if (!pts) return null
  const L = polyLength(pts)
  const w = totalOpeningWidth(opening)
  const s = (opening.params.hostOffsetFt as number) || 0
  // cheap gate: only an opening at (or pushed against) an end can overhang
  if (s > w / 2 + 1e-6 && s < L - w / 2 - 1e-6) return null
  const seams = wallSeams(host, walls)
  if (!seams.start && !seams.end) return null
  const ext: SeamExt = { before: seams.start?.extFt ?? 0, after: seams.end?.extFt ?? 0 }
  const c = wallFrame(pts).clampToSegment(s, w, ext)
  if (!c) return null
  const s0 = c.center - w / 2, s1 = c.center + w / 2
  const guests: OpeningSeamSplit['guests'] = []
  const guestCopy = (side: SeamSide, g0: number, g1: number) => {
    guests.push({
      wallId: side.partnerId,
      opening: { ...opening, params: { ...opening.params, __seamGuest: { s0: g0, s1: g1 } as unknown as ParamValue } },
    })
  }
  if (seams.start && s0 < -1e-6) {
    const sd = seams.start
    if (sd.partnerAtStart) guestCopy(sd, -s1, -s0)
    else guestCopy(sd, sd.partnerLen + s0, sd.partnerLen + s1)
  }
  if (seams.end && s1 > L + 1e-6) {
    const sd = seams.end
    if (sd.partnerAtStart) guestCopy(sd, s0 - L, s1 - L)
    else guestCopy(sd, sd.partnerLen - (s1 - L), sd.partnerLen - (s0 - L))
  }
  if (!guests.length) return null
  return {
    host: { ...opening, params: { ...opening.params, __seamExt: ext as unknown as ParamValue } },
    guests,
  }
}

/** opening id → the partner wall ids it overhangs (the docMirror dependency edge) */
export function openingSeamPartners(elements: ResolvedElement[]): Map<string, string[]> {
  const out = new Map<string, string[]>()
  let walls: ResolvedElement[] | null = null
  let byId: Map<string, ResolvedElement> | null = null
  for (const el of elements) {
    if ((el.category !== 'door' && el.category !== 'window') || !el.hostId) continue
    if (!walls) {
      walls = elements.filter((e) => e.category === 'wall')
      byId = new Map(walls.map((e) => [e.id as string, e]))
    }
    // EXTERIOR POCKETS: the room-side walls (the gap and the opening cut both derive
    // from them) — lazy import keeps openings.ts free of the opening category modules
    const room = el.category === 'door' && pocketRoomFinder ? pocketRoomFinder(el, byId!.get(el.hostId), walls) : null
    if (room) { out.set(el.id, room.walls); continue }
    const split = openingSeamSplit(el, byId!.get(el.hostId), walls)
    if (split) out.set(el.id, split.guests.map((g) => g.wallId))
  }
  return out
}

/** EXTERIOR POCKETS: registered by pocket.ts (it imports this module, so the edge runs
 *  one way at load time) */
let pocketRoomFinder: ((o: ResolvedElement, h: ResolvedElement | undefined, w: ResolvedElement[]) => { walls: string[] } | null) | null = null
export function registerPocketRoomFinder(fn: typeof pocketRoomFinder): void { pocketRoomFinder = fn }
